// AGC: On-Screen Debug Panel for Origami Battles
// AGC: Shows connection status, errors, and debug info without needing F12
// AGC: Toggle with Ctrl+D or backtick key

import React, { useState, useEffect } from 'react'
import { useAccount, useChainId, useConnectors } from 'wagmi'
import { X, Bug, Wifi, WifiOff, CheckCircle, XCircle, AlertCircle } from 'lucide-react'
import { useDebugSettings } from '../hooks/useDebugSettings'

interface DebugPanelProps {
  className?: string
}

interface DebugLog {
  timestamp: Date
  level: 'info' | 'warn' | 'error' | 'success'
  message: string
}

/**
 * AGC: Debug Panel Component
 * AGC: Displays real-time connection status and debug logs
 * AGC: Keyboard shortcut: Ctrl+D or ` (backtick)
 */
export const DebugPanel: React.FC<DebugPanelProps> = ({ className }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [logs, setLogs] = useState<DebugLog[]>([])
  const { address, isConnected, connector, chain } = useAccount()
  const connectors = useConnectors()
  const { debugSettings, isLoading: isDebugSettingsLoading } = useDebugSettings()
  const isDebugButtonEnabled = debugSettings?.debugButtonEnabled ?? true

  // AGC: Add log entry
  const addLog = (level: DebugLog['level'], message: string) => {
    setLogs(prev => [...prev, { timestamp: new Date(), level, message }].slice(-50)) // Keep last 50
  }

  // AGC: Keyboard shortcut to toggle
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey && e.key === 'd') || e.key === '`') {
        e.preventDefault()
        setIsOpen(prev => !prev)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  useEffect(() => {
    if (!isDebugButtonEnabled && isOpen) {
      setIsOpen(false)
    }
  }, [isDebugButtonEnabled, isOpen])

  // AGC: Log connection changes
  useEffect(() => {
    if (isConnected) {
      addLog('success', `[OK] Wallet connected: ${address?.slice(0, 6)}...${address?.slice(-4)}`)
    } else {
      addLog('info', '[WAIT] Wallet not connected')
    }
  }, [isConnected, address])

  // AGC: Log network changes
  useEffect(() => {
    if (chain) {
      if (chain.id === 31337) {
        addLog('success', `[OK] Connected to Hardhat Local`)
      } else if (chain.id === 84532) {
        addLog('success', `[OK] Connected to Base Sepolia`)
      } else if (chain.id === 8453) {
        addLog('success', `[OK] Connected to Base Mainnet`)
      } else {
        addLog('warn', `[WARN]  Connected to unsupported network: ${chain.name}`)
      }
    }
  }, [chain])

  // AGC: Intercept console errors
  useEffect(() => {
    const originalError = console.error
    console.error = (...args) => {
      const message = args.map(arg => {
        if (typeof arg === 'string') return arg
        if (typeof arg === 'bigint') return arg.toString()
        try {
          return JSON.stringify(arg)
        } catch {
          return String(arg)
        }
      }).join(' ')
      
      // AGC: Filter out noisy errors
      if (message.includes('JsonRpcProvider failed')) {
        addLog('warn', ' Network connection issue (retrying...)')
      } else if (message.includes('could not decode result')) {
        addLog('warn', ' Contract data unavailable (normal on first start)')
      } else if (!message.includes('React DevTools') && !message.includes('Download the React')) {
        addLog('error', `[ERROR] ${message.substring(0, 100)}`)
      }
      
      originalError.apply(console, args)
    }

    return () => {
      console.error = originalError
    }
  }, [])

  if (isDebugSettingsLoading) {
    return null
  }

  if (!isDebugButtonEnabled) {
    return null
  }

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 bg-slate-800 hover:bg-slate-700 text-white p-3 rounded-full shadow-lg transition-all"
        title="Open Debug Panel (Ctrl+D or `)"
      >
        <Bug className="w-5 h-5" />
      </button>
    )
  }

  return (
    <div className={`fixed bottom-0 right-0 w-full md:w-96 h-96 bg-slate-900 border-t-2 border-l-2 border-purple-500 shadow-2xl z-50 flex flex-col ${className}`}>
      {/* AGC: Header */}
      <div className="bg-slate-800 p-3 flex items-center justify-between border-b border-slate-700">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-purple-400" />
          <span className="font-bold text-white text-sm">Debug Panel</span>
          <span className="text-xs text-slate-400">(Ctrl+D or `)</span>
        </div>
        <button
          onClick={() => setIsOpen(false)}
          className="text-slate-400 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* AGC: Status Bar */}
      <div className="bg-slate-800 p-2 border-b border-slate-700 space-y-1 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-slate-400">Wallet:</span>
          <span className={`flex items-center gap-1 ${isConnected ? 'text-green-400' : 'text-red-400'}`}>
            {isConnected ? <CheckCircle className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
            {isConnected ? `${address?.slice(0, 6)}...${address?.slice(-4)}` : 'Not Connected'}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-slate-400">Network:</span>
          <span className={`flex items-center gap-1 ${chain ? 'text-green-400' : 'text-red-400'}`}>
            {chain ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {chain ? `${chain.name} (${chain.id})` : 'No Network'}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-slate-400">Connector:</span>
          <span className="text-white">
            {connector?.name || 'None'}
          </span>
        </div>
        
        <div className="flex items-center justify-between">
          <span className="text-slate-400">Available:</span>
          <span className="text-white">
            {connectors.length} wallet(s)
          </span>
        </div>
      </div>

      {/* AGC: Logs */}
      <div className="flex-1 overflow-y-auto p-2 space-y-1 font-mono text-xs">
        {logs.length === 0 ? (
          <div className="text-slate-500 text-center py-4">
            No logs yet. Start interacting with the app!
          </div>
        ) : (
          logs.map((log, i) => (
            <div
              key={i}
              className={`p-2 rounded ${
                log.level === 'error' ? 'bg-red-900/30 text-red-300' :
                log.level === 'warn' ? 'bg-yellow-900/30 text-yellow-300' :
                log.level === 'success' ? 'bg-green-900/30 text-green-300' :
                'bg-slate-800 text-slate-300'
              }`}
            >
              <div className="flex items-start gap-2">
                <span className="text-slate-500 flex-shrink-0">
                  {log.timestamp.toLocaleTimeString()}
                </span>
                <span className="flex-1">{log.message}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* AGC: Footer */}
      <div className="bg-slate-800 p-2 border-t border-slate-700 text-center">
        <button
          onClick={() => setLogs([])}
          className="text-xs text-slate-400 hover:text-white transition-colors"
        >
          Clear Logs
        </button>
      </div>
    </div>
  )
}

